import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Store } from '@ngrx/store';
import { interval, ReplaySubject } from 'rxjs';
import { takeUntil, throttle } from 'rxjs/operators';

import { coreSelectors } from 'app/core/state';

import { SceneService } from '../services/scene.service';
import { GroupShareScreenParticipantService } from '../services/share-screen-group/group-share-screen-participant.service';
import { Player } from '../social-pixi-canvas/player';
import { GroupScreenShare, socialAppSelectors } from '../state/app';

const INTERVAL = 1500;
@Component({
  selector: 'app-group-share-recording-banner',
  templateUrl: './group-share-recording-banner.component.html',
  styleUrls: ['./group-share-recording-banner.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GroupShareRecordingBannerComponent implements OnInit, OnDestroy {
  public isGroupShareRecording = false;
  protected userHasJoinedShare = false;
  protected userId?: string;
  private groupShareId?: string;
  private groupShares?: GroupScreenShare;
  private players: Player[] = [];
  private isUserSharingScreen = false;

  private destroyed$ = new ReplaySubject<boolean>(1);

  public constructor(
    private store: Store,
    private groupShareParticipantService: GroupShareScreenParticipantService,
    private sceneService: SceneService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    this.store
      .select(coreSelectors.getUserId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((userId) => {
        this.userId = userId;
        this.changeDetectorRef.markForCheck();
      });

    this.store
      .select(socialAppSelectors.isUserSharingScreenToGroup)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((userIsSharing) => {
        this.isUserSharingScreen = userIsSharing;
        this.changeDetectorRef.markForCheck();
      });

    this.store
      .select(socialAppSelectors.getGroupScreenShares)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((screenShares) => {
        this.groupShares = screenShares;
        this.handleRecording();
        this.changeDetectorRef.markForCheck();
      });

    this.store
      .select(socialAppSelectors.getMyGroupScreenShare)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((myGroupScrenShare) => {
        this.userHasJoinedShare = !!myGroupScrenShare;
        this.handleRecording();
        this.changeDetectorRef.markForCheck();
      });
  }

  public ngOnInit(): void {
    this.groupShareParticipantService.groupShareId$.pipe(takeUntil(this.destroyed$)).subscribe((id) => {
      this.groupShareId = id;
    });

    // Handles if participant joins group
    this.sceneService.primaryGroupMembers$
      .pipe(
        throttle(() => interval(INTERVAL), { trailing: true }),
        takeUntil(this.destroyed$),
      )
      .subscribe((players) => {
        this.players = players;
        this.handleRecording();
        this.changeDetectorRef.markForCheck();
      });
  }

  private handleRecording(): void {
    if (!this.groupShares) {
      return;
    }
    this.isGroupShareRecording = false;
    if (this.isUserSharingScreen) {
      const userId = this.userId ?? '';
      const screenShare = this.groupShares[userId];
      if (screenShare) {
        this.isGroupShareRecording = !!screenShare?.recordingId;
      }
    }

    this.players.forEach((user) => {
      const userId = user?.userId;
      if (!this.groupShares) {
        return;
      }
      const screenShare = this.groupShares[userId];
      if (screenShare) {
        this.isGroupShareRecording = !!screenShare?.recordingId;
      }
    });
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
